import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export function PaymentSuccess() {
  const { t, lang, clearCart, formatPrice } = useStore();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | error | timeout
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const res = await api.paymentStatus(sessionId);
        if (res.payment_status === 'paid') {
          setOrder(res.order); setState('paid');
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (res.status === 'expired' || res.payment_status === 'failed') { setState('error'); return; }
      } catch (e) { console.debug('Payment status poll retrying:', e?.message || e); }
      if (attempts.current >= 12) { setState('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="nw-container py-24 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg text-center">
        {state === 'polling' && (<><Loader2 size={40} className="animate-spin text-nw-gold mx-auto" /><p className="mt-6 text-nw-warmgrey">{t.common.loading}</p></>)}
        {state === 'paid' && (
          <>
            <CheckCircle2 size={48} className="text-nw-gold mx-auto" strokeWidth={1} />
            <h1 className="font-serif text-4xl text-nw-ivory mt-6">Merci</h1>
            <p className="mt-3 text-nw-warmgrey">{lang === 'fr' ? 'Votre commande est confirm\u00e9e.' : 'Your order is confirmed.'}</p>
            {order && (
              <div className="mt-8 bg-nw-espresso border border-nw-gold/20 p-6 text-left">
                <div className="flex justify-between text-sm"><span className="text-nw-warmgrey">{t.account.order}</span><span data-testid="payment-success-order-id" className="text-nw-gold font-medium tracking-wide">{order.order_id}</span></div>
                <div className="mt-3 pt-3 border-t border-nw-gold/15 flex justify-between"><span className="text-nw-warmgrey text-sm">{t.account.total}</span><span className="font-serif text-xl text-nw-gold">{formatPrice(order.total_eur)}</span></div>
              </div>
            )}
            <Link to="/collections" className="nw-btn-gold mt-8">{t.cart.continue}</Link>
          </>
        )}
        {(state === 'error' || state === 'timeout') && (
          <>
            <XCircle size={48} className="text-nw-gold mx-auto" strokeWidth={1} />
            <h1 className="font-serif text-3xl text-nw-ivory mt-6">{state === 'timeout' ? (lang === 'fr' ? 'Paiement en cours de v\u00e9rification' : 'Payment is being verified') : (lang === 'fr' ? 'Problème de paiement' : 'Payment issue')}</h1>
            <p className="mt-3 text-nw-warmgrey">{lang === 'fr' ? 'Si votre paiement a \u00e9t\u00e9 d\u00e9bit\u00e9, votre commande sera confirm\u00e9e sous peu.' : 'If your payment was captured, your order will be confirmed shortly.'}</p>
            <Link to="/collections" className="nw-btn-gold mt-8">{t.cart.continue}</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t, lang } = useStore();
  return (
    <div className="nw-container py-24 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg text-center">
        <XCircle size={48} className="text-nw-warmgrey mx-auto" strokeWidth={1} />
        <h1 className="font-serif text-3xl text-nw-ivory mt-6">{lang === 'fr' ? 'Paiement annul\u00e9' : 'Payment cancelled'}</h1>
        <p className="mt-3 text-nw-warmgrey">{lang === 'fr' ? 'Votre panier a \u00e9t\u00e9 conserv\u00e9.' : 'Your basket has been preserved.'}</p>
        <Link to="/cart" className="nw-btn-gold mt-8">{t.cart.title}</Link>
      </div>
    </div>
  );
}
